import React from 'react';
import { isNil } from 'lodash';
import {
  IPercentileFilterInput,
  IRelationalOperator,
  Maybe,
  RelationalOperatorKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { PrimaryButton, SecondaryButton } from '../../../../Button';
import BuilderCohortPercentileOperand from './CohortPercentileFilterOperand';
import usePercentileFormState from './usePercentileFormState';
import * as CohortPercentileFilterFormUI from './CohortPercentileFilterFormUI';
import { COHORT_PERCENTILE_FILTER_PLACEHOLDER } from '../../../../../utils/builder/cohorts';
import { cohortPercentileSchema } from './validation';
import { isZodFieldErrors, zodFieldErrors } from '../../../../../utils/zod';

interface IProps {
  onSubmit: (values: IPercentileFilterInput) => void;
  onReset: () => void;
  value: Maybe<IPercentileFilterInput>;
  percentileOperators: IRelationalOperator[];
}

export default ({ onSubmit, onReset, value, percentileOperators }: IProps) => {
  const { state, actions } = usePercentileFormState(value);

  const operatorOptions = percentileOperators.map((operator) => ({
    text: operator.name,
    value: operator.key,
  }));

  const safeParse = cohortPercentileSchema.safeParse(state);
  const isFormValid = safeParse.success;
  const errors = !safeParse.success ? safeParse.error : null;

  const handleReset = () => {
    actions.reset();

    onReset();
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      return;
    }

    const { operator } = safeParse.data;

    if (operator === RelationalOperatorKey.Between) {
      const { fromOperand, toOperand } = safeParse.data;

      onSubmit({
        operator,
        values: [fromOperand, toOperand],
      });

      return;
    }

    const { operand } = safeParse.data;

    onSubmit({
      operator,
      values: [operand],
    });
  };

  return (
    <>
      <CohortPercentileFilterFormUI.FormRow>
        <CohortPercentileFilterFormUI.OperatorMenu
          value={state.operator}
          onChange={actions.changeOperatorKey}
          options={operatorOptions}
        />
        {state.operator === RelationalOperatorKey.Between ? (
          <>
            <BuilderCohortPercentileOperand
              value={state.fromOperand}
              onChange={actions.changeFromOperand}
              placeholder="e.g. 5"
              hasError={isZodFieldErrors(errors, 'fromOperand')}
              errorMessage={zodFieldErrors(errors, 'fromOperand')}
              shouldAutoFocus={isNil(state.fromOperand)}
            />
            <CohortPercentileFilterFormUI.OperandDivider>
              ~
            </CohortPercentileFilterFormUI.OperandDivider>
            <BuilderCohortPercentileOperand
              value={state.toOperand}
              onChange={actions.changeToOperand}
              placeholder="e.g. 5"
              hasError={isZodFieldErrors(errors, 'toOperand')}
              errorMessage={zodFieldErrors(errors, 'toOperand')}
            />
          </>
        ) : (
          <BuilderCohortPercentileOperand
            value={state.operand}
            onChange={actions.changeOperand}
            placeholder="e.g. 5"
            hasError={isZodFieldErrors(errors, 'operand')}
            errorMessage={zodFieldErrors(errors, 'operand')}
            shouldAutoFocus={isNil(state.operand)}
          />
        )}
        <CohortPercentileFilterFormUI.Label>
          {COHORT_PERCENTILE_FILTER_PLACEHOLDER}
        </CohortPercentileFilterFormUI.Label>
      </CohortPercentileFilterFormUI.FormRow>
      <CohortPercentileFilterFormUI.ActionsContainer>
        <PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={!isFormValid}
          trackContext="cohort percentile"
          trackAction="apply"
        >
          Apply
        </PrimaryButton>
        <SecondaryButton
          type="button"
          onClick={handleReset}
          disabled={state.pristine && !value}
          trackContext="cohort percentile"
          trackAction="reset"
        >
          Reset
        </SecondaryButton>
      </CohortPercentileFilterFormUI.ActionsContainer>
    </>
  );
};
